// AddModal.js with Admin Toggle and Conditional Fields
import React, { useState, useEffect } from 'react';
import {
  Modal, Form, Input, Select, Switch,
  Button, Tabs, Spin, notification,
  InputNumber, Divider, Row, Col, Alert
} from 'antd';
import userService from '../../../../services/userService';
import config from '../../../../config';
import userRoleUtils from '../../../../utils/userRoleUtils';

const { Option } = Select;
const { TabPane } = Tabs;
const { TextArea } = Input;

const renderErrorDescription = (error) => {
  const errors = error?.errors || error?.response?.data?.errors;
  if (Array.isArray(errors) && errors.length > 0) {
    return (
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {errors.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    );
  }

  return error?.response?.data?.message || error?.response?.data?.error || error.message || 'Please check your inputs and try again.';
};

const AddModal = ({ visible, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [propertyRanges, setPropertyRanges] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [accountType, setAccountType] = useState('private');

  useEffect(() => {
    if (visible) {
      fetchPropertyRanges();
      form.resetFields();
      setIsAdmin(false);
      setAccountType('private');
      form.setFieldsValue({
        status: true,
        user_type: '0',
        service_type: 'LinenHire',
        number_of_properties: 3,
        country: 'GB',
        account_type: 'private',
        sync_to_schedox: false
      });
    }
  }, [visible, form]);

  const fetchPropertyRanges = async () => {
    try {
      const response = await userService.getPropertyRanges();
      setPropertyRanges(response.data || []);
    } catch (error) {
      notification.error({
        message: 'Error fetching property ranges',
        description: error.message || 'Failed to load property ranges'
      });
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const userData = {
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        secondary_email: values.secondary_email || '',
        phone: values.phone,
        password: values.password,
        password_confirmation: values.password,
        company_name: values.company_name || '',
        company_registration: values.company_registration || '',
        account_type: values.account_type || 'private',
        apartment_number: values.apartment_number || '',
        property_address: values.property_address || values.address || '',
        city: values.city || '',
        is_admin: values.is_admin || false,
        post_code: values.post_code || '',
        country: values.country || 'GB',
        service_type: values.service_type || 'LinenHire',
        number_of_properties: values.number_of_properties || values.property_range_id || 3,
        user_type: values.user_type || '0',
        status: values.status ? 1 : 0,
        sync_to_schedox: Boolean(values.sync_to_schedox)
      };
      const response = await userService.createUser(userData);
      if (response && response.status === true) {
        notification.success({
          message: 'User created successfully',
          description: response.message || 'The new user has been added to the system.'
        });
        form.resetFields();
        onSuccess();
      } else {
        const err = new Error(response.message || response.error || 'Failed to create user');
        err.errors = response.errors;
        throw err;
      }
    } catch (error) {
      notification.error({
        message: 'Failed to create user',
        description: renderErrorDescription(error)
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Add New User"
      open={visible}
      onCancel={onCancel}
      width={800}
      footer={[
        <Button key="back" onClick={onCancel}>Cancel</Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit} className="bg-golden-navy">
          Create User
        </Button>
      ]}
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            status: true,
            user_type: '0',
            service_type: 'LinenHire',
            number_of_properties: 3,
            country: 'GB',
            account_type: 'private'
          }}
        >
          <Form.Item label="Is Admin?" name="is_admin" valuePropName="checked">
            <Switch onChange={(checked) => setIsAdmin(checked)} />
          </Form.Item>

          {isAdmin ? (
            <>
              <Row gutter={16}>
                <Col span={12}><Form.Item name="first_name" label="First Name" rules={[{ required: true }]}><Input /></Form.Item></Col>
                <Col span={12}><Form.Item name="last_name" label="Last Name" rules={[{ required: true }]}><Input /></Form.Item></Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}><Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}><Input /></Form.Item></Col>
                <Col span={12}><Form.Item name="phone" label="Phone Number"><Input /></Form.Item></Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}><Form.Item name="password" label="Password" rules={[{ required: true }]}><Input.Password /></Form.Item></Col>
                <Col span={12}><Form.Item name="property_address" label="Address"><Input /></Form.Item></Col>
              </Row>
            </>
          ) : (
            <Tabs defaultActiveKey="basic">
              {/* original full UI including basic/company/address tabs */}
              <TabPane tab="Basic Information" key="basic">
                <Row gutter={16}>
                  <Col span={12}><Form.Item name="first_name" label="First Name" rules={[{ required: true }]}><Input /></Form.Item></Col>
                  <Col span={12}><Form.Item name="last_name" label="Last Name" rules={[{ required: true }]}><Input /></Form.Item></Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}><Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}><Input /></Form.Item></Col>
                  <Col span={12}><Form.Item name="secondary_email" label="Secondary Email" rules={[{ type: 'email' }]}><Input /></Form.Item></Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}><Form.Item name="password" label="Password" rules={[{ required: true }]}><Input.Password /></Form.Item></Col>
                  <Col span={12}><Form.Item name="phone" label="Phone Number"><Input /></Form.Item></Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="account_type" label="Account Type" rules={[{ required: true }]}>
                      <Select onChange={setAccountType}>
                        <Option value="private">Private</Option>
                        <Option value="company">Company</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}><Form.Item name="service_type" label="Service Type" rules={[{ required: true }]}><Select><Option value="LinenHire">Linen Hire</Option><Option value="BabyEquipment">Baby Equipment</Option><Option value="CleaningService">Cleaning Service</Option></Select></Form.Item></Col>
                  <Col span={12}><Form.Item name="number_of_properties" label="Property Range" rules={[{ required: true }]}><Select>{propertyRanges.map(range => (<Option key={range.id} value={range.id}>{range.range}</Option>))}</Select></Form.Item></Col>
                </Row>
                <Row gutter={16}><Col span={12}><Form.Item name="status" label="Status" valuePropName="checked"><Switch checkedChildren="Active" unCheckedChildren="Inactive" defaultChecked={false} /></Form.Item></Col></Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="sync_to_schedox"
                      label="Sync to Schedox"
                      valuePropName="checked"
                      extra="Turning this on enables all properties. Individual properties can then be switched off."
                    >
                      <Switch checkedChildren="On" unCheckedChildren="Off" />
                    </Form.Item>
                  </Col>
                </Row>
              </TabPane>
              {accountType === 'company' && <TabPane tab="Company Information" key="company">
                <Row gutter={16}>
                  <Col span={12}><Form.Item name="company_name" label="Company Name" rules={[{ required: true }]}><Input /></Form.Item></Col>
                  <Col span={12}><Form.Item name="company_registration" label="Company Registration Number"><Input /></Form.Item></Col>
                </Row>
              </TabPane>}
              <TabPane tab="Address Information" key="address">
                <Row gutter={16}>
                  <Col span={12}><Form.Item name="apartment_number" label="Apartment/Building Number"><Input /></Form.Item></Col>
                  <Col span={12}><Form.Item name="property_address" label="Address"><Input /></Form.Item></Col>
                </Row>
                <Row gutter={16}>
                  <Col span={8}><Form.Item name="city" label="City"><Input /></Form.Item></Col>
                  <Col span={8}><Form.Item name="post_code" label="Post Code"><Input /></Form.Item></Col>
                  <Col span={8}><Form.Item name="country" label="Country"><Select><Option value="GB">United Kingdom</Option><Option value="US">United States</Option><Option value="FR">France</Option><Option value="DE">Germany</Option><Option value="ES">Spain</Option><Option value="IT">Italy</Option><Option value="NL">Netherlands</Option></Select></Form.Item></Col>
                </Row>
              </TabPane>
            </Tabs>
          )}
        </Form>
      </Spin>
    </Modal>
  );
};

export default AddModal;
